import React, { useEffect, useRef, useState } from "react";
import { useIdleTimer } from "./AlertsUtils/useIdleTimer";
import {
  IDLE_GRACE_MS,
  LIVE_VIEW_IDLE_MS,
} from "./AlertsUtils/liveViewIdleConfig";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import {
  StreamProtocol,
  useWhepLiveStream,
  WhepLiveStreamStatus,
} from "./AlertsUtils/useWhepLiveStream";
import { ListenInButton, useListenInEnabled } from "./ListenInButton";
import { useIsTalkingToCamera } from "../../common/TalkDown/talkingCamerasStore";
import {
  useIsHoldingListenIn,
  useListenInHoldStore,
} from "./listenInHoldStore";
import { LiveViewFailureNotice } from "./LiveViewFailureNotice";
import {
  describeLiveViewFailure,
  isLiveViewFailureStatus,
  LIVE_STREAM_QUOTA_REACHED_MESSAGE,
  LiveViewFailure,
} from "./AlertsUtils/liveViewFailure";
import { useIsStaffUser } from "../../utils/staffUser";

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

// The copy lives in AlertsUtils/liveViewFailure (shared with the wall tiles);
// re-exported here for existing importers.
export {
  CAMERA_OFFLINE_MESSAGE,
  LIVE_STREAM_NOT_PERMITTED_MESSAGE,
  LIVE_STREAM_QUOTA_REACHED_MESSAGE,
  LIVE_VIEW_UNAVAILABLE_MESSAGE,
  SESSION_EXPIRED_MESSAGE,
} from "./AlertsUtils/liveViewFailure";

/**
 * Staff-only (ENG-2204): the camera's audio setting is on — the mint's
 * has_audio is derived from admin's allowed_media_types = video+audio — but
 * the stream came up without an audio track. Almost always the connector
 * hasn't picked the setting up yet (Sync + connector restart).
 */
export const AUDIO_NOT_STREAMING_STAFF_HINT =
  "Audio is on for this camera but not streaming yet — sync the site and restart it.";
/** Give the audio track a moment to show up before calling it missing. */
export const AUDIO_NOT_STREAMING_GRACE_MS = 5000;

export const STILL_WATCHING_MESSAGE = "Still watching?";
export const STILL_WATCHING_DETAIL =
  "Live view was paused after a period of inactivity to save your streaming allowance.";
export const STILL_WATCHING_COUNTDOWN = `Live view will pause in ${
  IDLE_GRACE_MS / 1000
} seconds unless you confirm you're still watching.`;
export const STILL_WATCHING_CONFIRM = "I'm still watching";

/**
 * Customer-facing failure copy for a status (+ the hook's failure reason when
 * known). No staff detail; see describeLiveViewFailure for that.
 */
export const getLiveViewUnavailableMessage = (
  status: WhepLiveStreamStatus,
  failure?: LiveViewFailure | null
): string => describeLiveViewFailure(status, failure).message;

interface LiveStreamPlayerProps {
  cameraId: string | null;
  enabled: boolean;
  protocol?: StreamProtocol;
  width?: number;
  height?: number;
  className?: string;
  /** Extra top offset for the LIVE badge (e.g. full-width live tab). */
  liveBadgeTopClass?: string;
  /** Talk-down control (ENG-2135), shown bottom-left over the frame. */
  talkDown?: React.ReactNode;
}

export const LiveStreamPlayer: React.FC<LiveStreamPlayerProps> = ({
  cameraId,
  enabled,
  protocol = "hls",
  width = 540,
  height = 304,
  className = "",
  liveBadgeTopClass = "top-3",
  talkDown,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Idle prompt (ENG-1132 / ENG-2149): the clock only runs once the stream is
  // actually PLAYING (a cold start can take longer than the idle window). After
  // the window with no activity, "Still watching?" shows over the live video;
  // if nobody confirms within the grace period the hook tears the player down
  // (publish stops → metering stops) and we show the paused prompt. Resume /
  // confirm = reset the timer; Resume also flips idlePaused off, which re-runs
  // the fresh-mint play() path. One window for every viewer — staff and
  // uncapped (24/7 opt-in) plans included (ENG-2149). The player hook and the
  // timer are mutually dependent (idlePaused → hook; status → timer), so idle
  // lives in state fed from the timer below. The timer stays armed through
  // idle_paused so the pause isn't cleared out from under the hook.
  const [idle, setIdle] = useState(false);
  const {
    status,
    failure,
    retry,
    overageAvailable,
    continueWithOverage,
    hasAudio,
    mintHasAudio,
    talkDownCapable,
  } = useWhepLiveStream(cameraId, enabled, videoRef, protocol, true, idle);
  // ENG-1721: the caller's talkDownCapable (from the alert row) wins when it
  // is a definite answer; when it is absent or null (not probed), fill it
  // from this stream's mint if the mint has one. Read-only, no new requests.
  const talkDownControl =
    React.isValidElement<{ talkDownCapable?: boolean | null }>(talkDown) &&
    talkDown.props.talkDownCapable == null &&
    typeof talkDownCapable === "boolean"
      ? React.cloneElement(talkDown, { talkDownCapable })
      : talkDown;
  // Listen-in / talk-down state (ENG-2136) — read up here because an active
  // talk-down hold or listen-in hold is engagement with the stream: it stops
  // the idle clock and dismisses a showing "Still watching?". The listen-in
  // TOGGLE deliberately does not: left on, it would keep an unattended stream
  // (and its metering) alive forever, which is what the idle prompt prevents.
  const talking = useIsTalkingToCamera(cameraId);
  const holdingListenIn = useIsHoldingListenIn(cameraId);
  const [toggleListening, setToggleListening] = useState(false);
  const idleTimerArmed =
    enabled && (status === "playing" || status === "idle_paused");
  const idleHeld = talking || holdingListenIn;
  const {
    idle: timerIdle,
    prompting: stillWatchingPrompt,
    reset: resumeFromIdle,
  } = useIdleTimer(idleTimerArmed, LIVE_VIEW_IDLE_MS, IDLE_GRACE_MS, idleHeld);
  useEffect(() => {
    setIdle(timerIdle);
  }, [timerIdle]);

  const showVideo = enabled && status === "playing";

  // Listen-in (ENG-2136). `toggleListening` is the operator's click-to-stay
  // choice on the overlay button; `holdingListenIn` is the Option A ear
  // hold-to-listen. The element is muted unless either is active AND nobody
  // is talking down to this camera (half-duplex — the toggle choice survives
  // a talk-down hold). Every new stream (anything but "playing", incl. a
  // mid-play restart or a camera switch) starts muted again.
  const listenInEnabled = useListenInEnabled();
  const setHasAudio = useListenInHoldStore((s) => s.setHasAudio);
  const talkingRef = useRef(talking);
  talkingRef.current = talking;
  const holdingListenInRef = useRef(holdingListenIn);
  holdingListenInRef.current = holdingListenIn;
  useEffect(() => {
    if (!showVideo) setToggleListening(false);
  }, [showVideo, cameraId]);
  useEffect(() => {
    if (cameraId == null || cameraId === "") return;
    if (!enabled) {
      setHasAudio(cameraId, null);
      return () => setHasAudio(cameraId, null);
    }
    // Mint veto: cross out the Option A ear immediately (no need to wait for
    // media). Once playing, publish the real has-audio answer so a video-only
    // stream also crosses the ear out. While loading, leave it unknown so the
    // ear stays optimistic instead of flashing unavailable.
    if (mintHasAudio === false) {
      setHasAudio(cameraId, false);
    } else if (showVideo) {
      setHasAudio(cameraId, hasAudio);
    } else {
      setHasAudio(cameraId, null);
    }
    return () => setHasAudio(cameraId, null);
  }, [cameraId, enabled, mintHasAudio, showVideo, hasAudio, setHasAudio]);
  const listening = toggleListening || holdingListenIn;
  const muted = !listening || talking;
  useEffect(() => {
    const video = videoRef.current;
    if (video && video.muted !== muted) video.muted = muted;
  }, [muted]);
  // The native controls have their own mute button: keep our state in step
  // with it, and never let it unmute through a talk-down hold. Ear-button
  // holds also drive muted programmatically — don't latch those into the
  // click-to-stay toggle.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onVolumeChange = () => {
      if (talkingRef.current) {
        if (!video.muted) video.muted = true;
        return;
      }
      if (holdingListenInRef.current) return;
      setToggleListening(!video.muted);
    };
    video.addEventListener("volumechange", onVolumeChange);
    return () => video.removeEventListener("volumechange", onVolumeChange);
  }, []);
  const showListenIn = showVideo && listenInEnabled && hasAudio;
  // Setting on (mint has_audio true) + playing + no audio track → staff hint,
  // after a grace period so a track that arrives a beat late doesn't flash it.
  const staff = useIsStaffUser();
  const audioMissing =
    showVideo && listenInEnabled && staff && mintHasAudio === true && !hasAudio;
  const [showAudioMissingHint, setShowAudioMissingHint] = useState(false);
  useEffect(() => {
    if (!audioMissing) {
      setShowAudioMissingHint(false);
      return;
    }
    const timer = setTimeout(
      () => setShowAudioMissingHint(true),
      AUDIO_NOT_STREAMING_GRACE_MS
    );
    return () => clearTimeout(timer);
  }, [audioMissing, cameraId]);
  const showIdlePaused = enabled && status === "idle_paused";
  const showLoading =
    enabled &&
    (status === "loading" || status === "idle" || status === "starting");
  const showUnavailable = enabled && isLiveViewFailureStatus(status);

  return (
    <div className={`relative bg-black ${className}`} style={{ width, height }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        controls
        className="block h-full w-full object-contain"
        style={{ display: showVideo ? "block" : "none" }}
      />
      {showVideo && (
        <div
          className={`pointer-events-none absolute left-3 z-10 inline-flex items-center gap-1.5 rounded bg-[#D93636] px-2.5 py-1.5 font-mulish text-[11px] font-bold uppercase tracking-[0.08em] text-white shadow-[0_2px_8px_rgba(217,54,54,0.4)] ${liveBadgeTopClass}`}
          aria-label="Live stream"
        >
          <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-white" />
          <span>LIVE</span>
        </div>
      )}
      {showListenIn && (
        <div className="absolute bottom-12 right-3 z-30">
          <ListenInButton
            listening={toggleListening || holdingListenIn}
            talking={talking}
            onToggle={() => setToggleListening((prev) => !prev)}
          />
        </div>
      )}
      {showAudioMissingHint && (
        <p
          className="pointer-events-none absolute right-3 top-3 z-10 max-w-[70%] rounded bg-black/60 px-2 py-1 text-right font-mono text-[11px] leading-snug text-white/70"
          data-testid="listen-in-audio-missing-hint"
        >
          {AUDIO_NOT_STREAMING_STAFF_HINT}
        </p>
      )}
      {enabled && talkDown && (
        // Above the <video> controls bar so the button stays reachable while
        // the stream plays; the control gates itself and may render nothing.
        <div className="absolute bottom-12 left-3 z-30">{talkDownControl}</div>
      )}
      {showVideo && stillWatchingPrompt && (
        // Centred card under the talk-down / listen-in controls (z-30), so
        // those stay clickable while it shows — and using them dismisses it.
        <div
          className="absolute left-1/2 top-1/2 z-20 flex w-[calc(100%-24px)] max-w-[360px] -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded bg-black/85 px-4 py-3 shadow-lg"
          data-testid="live-still-watching-prompt"
          role="alertdialog"
          aria-label={STILL_WATCHING_MESSAGE}
        >
          <p className="font-mulish text-center text-[16px] font-bold text-white">
            {STILL_WATCHING_MESSAGE}
          </p>
          <p className="font-mulish mt-1 text-center text-[12px] text-white/80">
            {STILL_WATCHING_COUNTDOWN}
          </p>
          <button
            type="button"
            onClick={resumeFromIdle}
            className="font-mulish mt-2 rounded bg-[#FF8400] px-4 py-1.5 text-[13px] font-bold text-white hover:bg-[#e67700]"
          >
            {STILL_WATCHING_CONFIRM}
          </button>
        </div>
      )}
      {showIdlePaused && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-black px-4"
          data-testid="live-idle-prompt"
        >
          <p className="font-mulish text-center text-[20px] font-bold text-white">
            {STILL_WATCHING_MESSAGE}
          </p>
          <p className="font-mulish mt-2 text-center text-[14px] text-white/80">
            {STILL_WATCHING_DETAIL}
          </p>
          <button
            type="button"
            onClick={resumeFromIdle}
            className="font-mulish mt-4 rounded bg-[#FF8400] px-4 py-2 text-[14px] font-bold text-white hover:bg-[#e67700]"
          >
            Resume
          </button>
        </div>
      )}
      {(showLoading || showUnavailable) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
          {showLoading ? (
            <>
              <ThemeProvider theme={theme}>
                <CircularProgress color="primary" />
              </ThemeProvider>
              <p className="font-mulish mt-4 text-center text-[16px] text-white">
                {status === "starting"
                  ? "Starting camera stream — this can take up to a minute..."
                  : "Connecting live view..."}
              </p>
            </>
          ) : status === "quota_reached" && overageAvailable ? (
            // Soft cap (ENG-721): the mint offered overage. One explicit
            // click is the billing consent — the hook re-mints with
            // overage_opt_in and its keepalives carry it for this view.
            <>
              <p className="font-mulish text-center text-[18px] text-white">
                {LIVE_STREAM_QUOTA_REACHED_MESSAGE}
              </p>
              <p className="font-mulish mt-2 text-center text-[14px] text-white/80">
                You can keep streaming this camera — additional charges will
                apply to your account.
              </p>
              <button
                type="button"
                onClick={continueWithOverage}
                className="font-mulish mt-4 rounded bg-[#FF8400] px-4 py-2 text-[14px] font-bold text-white hover:bg-[#e67700]"
              >
                Continue streaming
              </button>
            </>
          ) : (
            <LiveViewFailureNotice
              status={status}
              failure={failure}
              onRetry={retry}
              variant="player"
            />
          )}
        </div>
      )}
    </div>
  );
};

export type { WhepLiveStreamStatus };
